import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import SearchCountScatterplot from "./SearchCountScatterplot";
import DataSvc from "../../../../services/dataSvc";
import DropdownSelect from "../../../FormElement/DropdownSelect";
import { FeatureNames } from "../types/featureNames";
import { KeywordReviewCrossAnalysisV2Response } from "../types/keywordReviewCrossAnalysisV2Data";

import "./styles.scss";

interface IKeywordReviewCrossAnalysisV2Props {
  organizationId: string;
}

const KeywordReviewCrossAnalysisV2: React.FunctionComponent<
  IKeywordReviewCrossAnalysisV2Props
> = ({ organizationId }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`EXPERIMENTAL_organizationFeaturesPage.v2.${key}`);

  const [selectedLocationId, setSelectedLocationId] = React.useState<string>("");

  const query = useQuery<KeywordReviewCrossAnalysisV2Response>({
    queryKey: [
      "experimentalOrganizationFeature",
      FeatureNames.KEYWORD_REVIEW_CROSS_ANALYSIS_V2,
      organizationId,
    ],
    queryFn: () =>
      DataSvc.EXPERIMENTAL_getOrganizationFeatures({
        featureName: FeatureNames.KEYWORD_REVIEW_CROSS_ANALYSIS_V2,
        organizationId,
      }),
    enabled: !!organizationId,
    onSuccess: (result) => {
      const locationIds = Object.keys(result.data ?? {});
      if (locationIds.length > 0 && !selectedLocationId) {
        setSelectedLocationId(locationIds[0]);
      }
    },
  });

  const formatRatio = (reviewCount: number, searchCount: number): string => {
    if (searchCount === 0) return "∞";
    return `${((reviewCount / searchCount) * 100).toFixed(2)}%`;
  };

  const allData = query.data?.data;
  const updatedAt = query.data?.last_modified_at
    ? dayjs(query.data.last_modified_at).format("YYYY-MM-DD HH:mm")
    : null;
  const locationIds = Object.keys(allData ?? {});
  const activeLocationId = selectedLocationId || locationIds[0] || "";
  const locationLabel = (id: string) =>
    `${id}${allData?.[id] ? ` — ${allData[id].store}` : ""}`;
  const locationData = allData?.[activeLocationId];
  const topKeyword = locationData?.top_keywords?.[0];
  const furtherSuggestions = locationData?.top_keywords?.slice(1) ?? [];

  return (
    <div className="keyword-review-cross-analysis-v2-container">
      {allData && locationIds.length > 0 && (
        <DropdownSelect
          value={locationLabel(activeLocationId)}
          items={locationIds.map(locationLabel)}
          onChange={(label) =>
            setSelectedLocationId(label.split(" — ")[0])
          }
        />
      )}
      {updatedAt && (
        <span className="keyword-review-cross-analysis-v2-upload-filename">
          {t("last_uploaded")}: {updatedAt}
        </span>
      )}

      {locationData && (
        <>
          <p className="keyword-review-cross-analysis-v2-store-name">{locationData.store}</p>

          {topKeyword && (
            <div className="white-border-panel keyword-review-cross-analysis-v2-top-keyword">
              <div className="keyword-review-cross-analysis-v2-top-keyword-label">{t("top_keyword")}</div>
              <div className="keyword-review-cross-analysis-v2-top-keyword-name">
                {topKeyword.keyword}
              </div>
              <div className="keyword-review-cross-analysis-v2-top-keyword-stats">
                <div className="keyword-review-cross-analysis-v2-stat">
                  <span className="keyword-review-cross-analysis-v2-stat-value">
                    {new Intl.NumberFormat("en").format(
                      topKeyword.search_count_total
                    )}
                  </span>
                  <span className="keyword-review-cross-analysis-v2-stat-label">{t("search_count")}</span>
                </div>
                <div className="keyword-review-cross-analysis-v2-stat">
                  <span className="keyword-review-cross-analysis-v2-stat-value">
                    {new Intl.NumberFormat("en").format(
                      topKeyword.review_mention_count
                    )}
                  </span>
                  <span className="keyword-review-cross-analysis-v2-stat-label">{t("review_mentions")}</span>
                </div>
                <div className="keyword-review-cross-analysis-v2-stat">
                  <span className="keyword-review-cross-analysis-v2-stat-value">
                    {formatRatio(
                      topKeyword.review_mention_count,
                      topKeyword.search_count_total
                    )}
                  </span>
                  <span className="keyword-review-cross-analysis-v2-stat-label">{t("ratio")}</span>
                </div>
              </div>
              <p className="keyword-review-cross-analysis-v2-top-keyword-reason">{topKeyword.reason}</p>
            </div>
          )}

          <div className="white-border-panel keyword-review-cross-analysis-v2-chart-panel">
            <h3>{t("chart_title")}</h3>
            <SearchCountScatterplot keywords={locationData.top_keywords} />
          </div>

          {furtherSuggestions.length > 0 && (
            <>
              <h3 className="keyword-review-cross-analysis-v2-suggestions-title">{t("further_suggestions")}</h3>
              <div className="keyword-review-cross-analysis-v2-suggestions">
                {furtherSuggestions.map((kw, index) => (
                  <div
                    key={kw.keyword}
                    className="white-border-panel keyword-review-cross-analysis-v2-suggestion-item"
                  >
                    <div className="keyword-review-cross-analysis-v2-suggestion-header">
                      <span className="keyword-review-cross-analysis-v2-suggestion-rank">
                        #{index + 2}
                      </span>
                      <span className="keyword-review-cross-analysis-v2-suggestion-name">
                        {kw.keyword}
                      </span>
                      <div className="keyword-review-cross-analysis-v2-suggestion-stats">
                        <span>
                          {t("search")}:{" "}
                          {new Intl.NumberFormat("en").format(
                            kw.search_count_total
                          )}
                        </span>
                        <span>
                          {t("reviews")}:{" "}
                          {new Intl.NumberFormat("en").format(
                            kw.review_mention_count
                          )}
                        </span>
                        <span>
                          {t("ratio")}:{" "}
                          {formatRatio(
                            kw.review_mention_count,
                            kw.search_count_total
                          )}
                        </span>
                      </div>
                    </div>
                    <p className="keyword-review-cross-analysis-v2-suggestion-reason">{kw.reason}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default KeywordReviewCrossAnalysisV2;
